import { useMemo, useState, useEffect } from "react";
import config from "../config/env";
import { anomalySummaryApi } from "@/api/anomalySummaryApi";

// AnomalyDetectionSummary.js
// Single-file React component using Tailwind CSS that matches the provided design.
// - Top summary cards (Total / High / Medium / Low)
// - Filter row (Severity, Start date/time, End date/time, Apply Filter)
// - Table with Event Time, No of Metrics, Metrics Involved, Severity, Flag
// - Tooltip on metrics link
// - Pagination (simple)

// API Configuration from environment
const API_BASE_URL = config.api.anomalyUrl;

export default function AnomalyDetectionSummary() {
  // Filter input states
  const [severityInput, setSeverityInput] = useState<string>("All");
  const [startDate, setStartDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");

  const [appliedFilters, setAppliedFilters] = useState<{
    severity: string;
    start: Date | null;
    end: Date | null;
  }>({
    severity: "All",
    start: null,
    end: null,
  });

  // API states
  const [apiData, setApiData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch anomalies from API
  const fetchAnomalies = async (
    startDateTime: string | null = null,
    endDateTime: string | null = null
  ) => {
    setLoading(true);
    setError(null);

    try {
      const data = await anomalySummaryApi.getSummary(startDateTime, endDateTime);
      console.log("API Response:", data);
      setApiData(data);
    } catch (err: any) {
      console.error("Error fetching anomalies:", err);
      setError(err?.message || "Failed to fetch anomalies");
      // Set empty data on error
      setApiData(null);
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    const parseDateTime = (d: string, t: string) => {
      if (!d) return null;
      const timePart = t || "00:00";
      const iso = `${d}T${timePart}:00`;
      const dt = new Date(iso);
      return isNaN(dt.getTime()) ? null : dt;
    };

    const start = parseDateTime(startDate, startTime);
    const end = parseDateTime(endDate, endTime);

    setAppliedFilters({ severity: severityInput, start, end });

    // Fetch data from API with date range (only if both dates are provided)
    if (startDate && endDate) {
      const startISO = `${startDate}T${startTime || "00:00"}:00`;
      const endISO = `${endDate}T${endTime || "00:00"}:00`;
      fetchAnomalies(startISO, endISO);
    } else {
      // If no date range provided, fetch without parameters
      fetchAnomalies();
    }
  };

  const clearFilters = () => {
    setSeverityInput("All");
    setStartDate("");
    setStartTime("");
    setEndDate("");
    setEndTime("");
    setAppliedFilters({ severity: "All", start: null, end: null });
    // Fetch data without parameters (default API behavior)
    fetchAnomalies();
  };

  // Fetch data on component mount
  useEffect(() => {
    // Initial fetch without parameters (gets all anomalies)
    fetchAnomalies();
    // We only want to fetch on mount, not on every fetchAnomalies change
  }, []);

  // Extract actual anomaly data from the API response
  const anomalyData = useMemo(() => {
    return apiData?.data || [];
  }, [apiData]);

  const filtered = useMemo(() => {
    const { severity, start, end } = appliedFilters;

    return anomalyData
      .filter((a: any) => {
        // Severity filter
        if (severity !== "All" && a.severity !== severity) {
          return false;
        }

        // Date/Time range filter
        if (start || end) {
          const t = new Date(a.eventTime);
          if (start && t < start) return false;
          if (end && t > end) return false;
        }

        return true;
      })
      .sort((x: any, y: any) => new Date(y.eventTime).getTime() - new Date(x.eventTime).getTime());
  }, [appliedFilters, anomalyData]);

  const summary = useMemo(() => {
    // Calculate from filtered data so summary updates when filters are applied
    const total = filtered.length;
    const high = filtered.filter((f: any) => f.severity === "High").length;
    const medium = filtered.filter((f: any) => f.severity === "Medium").length;
    const low = filtered.filter((f: any) => f.severity === "Low").length;
    return { total, high, medium, low };
  }, [filtered]);

  const [page, setPage] = useState(1);
  const pageSize = 10; // Increased from 4 to 10 for better visibility
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageData = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => setPage(1), [filtered.length]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Page Header with API Status */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Anomaly Detection Summary</h1>
        <div className="flex items-center gap-4">
          {!loading && !error && (
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <span className="text-gray-600">Live API data</span>
            </div>
          )}
          {!loading && error && (
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
              <span className="text-red-600">API Error</span>
            </div>
          )}
          <button
            onClick={() => {
              // Refresh with current date filters if provided, otherwise without parameters
              if (startDate && endDate) {
                fetchAnomalies(
                  `${startDate}T${startTime || "00:00"}:00`,
                  `${endDate}T${endTime || "00:00"}:00`
                );
              } else {
                fetchAnomalies();
              }
            }}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
          >
            <span className={loading ? "animate-spin" : ""}>🔄</span>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span className="text-blue-700">Loading anomaly data from API...</span>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <span className="text-red-500 text-xl">⚠️</span>
            <div>
              <p className="text-red-700 font-semibold">Error loading data from API</p>
              <p className="text-red-600 text-sm">{error}</p>
              <p className="text-red-500 text-xs mt-1">
                Please check your API connection and try again
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-6 mb-6">
        <Card
          bgClass="bg-blue-100"
          title="Total Anomalies"
          value={summary.total}
          loading={loading}
        />
        <Card bgClass="bg-red-100" title="High Severity" value={summary.high} loading={loading} />
        <Card bgClass="bg-orange-100" title="Medium" value={summary.medium} loading={loading} />
        <Card bgClass="bg-yellow-100" title="Low" value={summary.low} loading={loading} />
      </div>

      <div className="bg-white rounded shadow-sm p-6">
        {/* Filter Status Badge */}
        {(appliedFilters.severity !== "All" || appliedFilters.start || appliedFilters.end) && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-blue-800">Active Filters:</span>
              {appliedFilters.severity !== "All" && (
                <span className="px-2 py-1 bg-blue-100 rounded text-blue-700">
                  Severity: {appliedFilters.severity}
                </span>
              )}
              {appliedFilters.start && (
                <span className="px-2 py-1 bg-blue-100 rounded text-blue-700">
                  From: {appliedFilters.start.toLocaleString()}
                </span>
              )}
              {appliedFilters.end && (
                <span className="px-2 py-1 bg-blue-100 rounded text-blue-700">
                  To: {appliedFilters.end.toLocaleString()}
                </span>
              )}
              <span className="ml-2 text-blue-700">
                ({filtered.length} result{filtered.length !== 1 ? "s" : ""} found)
              </span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-gray-700">Anomaly Intelligence Board</h2>

          <div className="flex gap-3 items-center">
            <div className="flex items-center gap-2">
              <label className="sr-only">Severity</label>
              <select
                value={severityInput}
                onChange={(e) => setSeverityInput(e.target.value)}
                className="border rounded px-3 py-2 w-40"
              >
                <option value="All">All Severities</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <label className="text-sm text-gray-500">Start Date/Time</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border rounded px-3 py-2 w-44"
                    placeholder="Start date"
                  />
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="border rounded px-3 py-2 w-24"
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <label className="text-sm text-gray-500">End Date/Time</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border rounded px-3 py-2 w-44"
                    placeholder="End date"
                  />
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="border rounded px-3 py-2 w-24"
                  />
                </div>
              </div>

              <button
                onClick={applyFilter}
                className="ml-2 border rounded px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
              >
                Apply Filter
              </button>
              <button
                onClick={clearFilters}
                className="border rounded px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-blue-50 text-left text-sm text-gray-600">
                <th className="py-3 px-6">Event Time</th>
                <th className="py-3 px-6">No of Metrics</th>
                <th className="py-3 px-6">Metrics Involved</th>
                <th className="py-3 px-6">Severity</th>
                <th className="py-3 px-6">Flag</th>
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    No anomalies found for the applied filter.
                  </td>
                </tr>
              )}

              {pageData.map((row: any) => (
                <tr key={row.id} className="border-b">
                  <td className="py-6 px-6 align-top text-sm text-gray-700">
                    {formatDate(row.eventTime)}
                  </td>
                  <td className="py-6 px-6 align-top text-sm text-gray-700">{row.metricsCount}</td>
                  <td className="py-6 px-6 align-top text-sm text-gray-700">
                    {row.metricsCount > 0 ? (
                      <MetricsLink metrics={row.metrics} />
                    ) : (
                      <span className="text-gray-400">Correlated Metrics</span>
                    )}
                  </td>
                  <td className="py-6 px-6 align-top text-sm text-gray-700">
                    <SeverityDot severity={row.severity} />{" "}
                    <span className="ml-2">{row.severity}</span>
                  </td>
                  <td className="py-6 px-6 align-top text-sm text-gray-700">
                    <SeverityFlag severity={row.severity} flagged={row.flagged} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-600">
            Showing {pageData.length > 0 ? (page - 1) * pageSize + 1 : 0} -{" "}
            {Math.min(page * pageSize, filtered.length)} of {filtered.length} anomalies
            {filtered.length !== anomalyData.length && (
              <span className="ml-2 text-blue-600 font-medium">
                (filtered from {anomalyData.length} total)
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-2 rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              disabled={page === 1}
            >
              ◀
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full border flex items-center justify-center bg-white">
                {page}
              </div>
              <span className="text-gray-500">of {pageCount}</span>
            </div>
            <button
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              className="p-2 rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              disabled={page === pageCount}
            >
              ▶
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ bgClass, title, value, loading }: any) {
  return (
    <div className={`rounded-lg p-4 flex-1 ${bgClass}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">🔔</div>
          <div>
            <div className="text-sm text-gray-600">{title}</div>
            {loading ? (
              <div className="h-7 w-12 bg-gray-200 animate-pulse rounded mt-1"></div>
            ) : (
              <div className="text-xl font-semibold">{value}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SeverityDot({ severity }: any) {
  const color =
    severity === "High" ? "bg-red-500" : severity === "Medium" ? "bg-orange-400" : "bg-yellow-400";
  return <span className={`inline-block w-3 h-3 rounded-full ${color}`} />;
}

function SeverityFlag({ severity, flagged }: any) {
  if (!flagged) {
    return <span className="inline-block p-2 rounded bg-green-50 text-green-500 text-lg">✓</span>;
  }

  // Color based on severity for flagged items
  let bgColor, textColor;
  switch (severity) {
    case "High":
      bgColor = "bg-red-50";
      textColor = "text-red-600";
      break;
    case "Medium":
      bgColor = "bg-orange-50";
      textColor = "text-orange-600";
      break;
    case "Low":
      bgColor = "bg-yellow-50";
      textColor = "text-yellow-600";
      break;
    default:
      bgColor = "bg-gray-50";
      textColor = "text-gray-600";
  }

  return (
    <span className={`inline-block p-2 rounded ${bgColor} ${textColor} font-bold text-xl`}>⚑</span>
  );
}

function MetricsLink({ metrics }: any) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="underline text-blue-600 text-sm"
      >
        {metrics.map((m: any) => m.name).join(", ")}
      </button>

      {open && (
        <div className="absolute left-36 top-0 w-48 bg-yellow-50 border-2 border-yellow-200 rounded-md p-3 shadow-sm z-10">
          {metrics.map((m: any, idx: number) => (
            <div key={idx} className="text-sm text-gray-700 mb-1">
              <div className="font-semibold">{m.name}</div>
              {m.actual != null && (
                <div className="text-xs text-gray-600">
                  Actual:{" "}
                  <span className="font-medium text-black">
                    {Number.isFinite(Number(m.actual))
                      ? Number(m.actual).toFixed(2)
                      : String(m.actual)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}
